import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CheckboxContainer,
  ErrorMessage,
  Form,
  GlobalStyle,
  Logo,
  MainContent,
  OTPInputBox,
  OTPInputContainer,
  PageContainer,
  Sidebar,
  Subtitle,
  Title,
} from './BetaComponents';

type RegisterRequest = {
  user_number: string;
  first_name: string;
  last_name: string;
  secretary_number: string;
  user_email: string;
};

type OTPInfo =
  | { purpose: 'login'; user_number: string, user_id: string}
  | { purpose: 'signup'; info: RegisterRequest };

function getUserNumber(otpInfo: OTPInfo): string {
  switch (otpInfo.purpose) {
    case 'login':
      return otpInfo.user_number;
    case 'signup':
      return otpInfo.info.user_number;
  }
}

const OTPPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const otpInfo = location.state as OTPInfo;
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendTimer = () => {
    setResendTimer(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!otpInfo) {
      navigate('/get-started');
    } else {
      inputRefs.current[0]?.focus();
      startResendTimer();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleResendOTP = async () => {
    if (!otpInfo || resendLoading || resendTimer > 0) return;
    setResendLoading(true);
    setError(null);
    setOtp(new Array(6).fill(''));

    try {
      const response = await fetch('/v0/otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: getUserNumber(otpInfo) }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to resend OTP.');
      }
      startResendTimer();
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    if (element.value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text');
    if (paste.length === 6 && /^\d+$/.test(paste)) {
      const newOtp = paste.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const otpCode = otp.join('');
    try {
      const body = {
        otp_info: otpInfo,
        otp: otpCode,
      };

      const response = await fetch('/v0/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'An error occurred.');
        setLoading(false);
        return;
      }

      // Redirect to account page
      if (data.redirect_url) {
        window.location.href = data.redirect_url._url;
      } 
    } catch (err) {
      setError('Unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <Sidebar>
          <Logo />
        </Sidebar>
        <MainContent>
          <Title>Enter One Time Verification Code</Title>
          {otpInfo && (
            <Subtitle>Sent to: {getUserNumber(otpInfo)}</Subtitle>
          )}
          <Form onSubmit={handleSubmit}>
            <OTPInputContainer>
              {otp.map((data, index) => {
                return (
                  <OTPInputBox
                    key={index}
                    type='text'
                    name='otp'
                    maxLength={1}
                    value={data}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    onPaste={index === 0 ? handlePaste : undefined}
                    ref={(el) => (inputRefs.current[index] = el)}
                    disabled={loading}
                  />
                );
              })}
            </OTPInputContainer>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type='submit' disabled={loading || otp.join('').length < 6}>
              {loading ? 'Checking...' : 'Next'}
            </Button>
          </Form>
          <CheckboxContainer>
            <button
              onClick={handleResendOTP}
              disabled={resendTimer > 0 || resendLoading}
              style={{
                background: 'none',
                border: 'none',
                color: resendTimer > 0 || resendLoading ? '#888' : '#000',
                fontFamily: "Plus Jakarta Sans",
                fontSize: '14px',
                fontWeight: 600,
                cursor: resendTimer > 0 || resendLoading ? 'default' : 'pointer',
              }}
            >
              {resendLoading
                ? 'Sending...'
                : resendTimer > 0
                ? `Resend OTP in ${resendTimer}s`
                : "Didn't receive your OTP? Resend"}
            </button>
          </CheckboxContainer>
        </MainContent>
      </PageContainer>
    </>
  );
};

export default OTPPage;
